import { CameraAnimationProps } from "@/components/r3f/animations/camera-animation";
import { PHOTO_SIZE } from "@/object-cache";
import {
  OnlyIElementsStore,
  selectIElementsSlice,
} from "@/store/ielements-slice-selector";
import { curryAppSelector } from "@/store/reselect";
import { RootState } from "@/store/store";
import {
  isOrthographicCamera,
  isPerspectiveCamera,
} from "@/types/threejs-type-guards";
import {
  IElementsState,
  parseVector3,
  selectIElementWorldTransform,
  type OrthoFrustum,
} from "@faro-lotv/app-component-toolbox";
import { IElementImg2d } from "@faro-lotv/ielement-types";
import { DEFAULT_PERSP_FOV, DEG_TO_RAD } from "@faro-lotv/lotv";
import { createSelector } from "@reduxjs/toolkit";
import { Camera, Quaternion, Vector3 } from "three";
import {
  ANIMATION_DURATION,
  FlyWalkSceneActiveElement,
} from "../flywalk-types";
import {
  FlyWalkAnimation,
  FlyWalkRenderingAnimation,
} from "./flywalk-animation";
import { runToImgAnimation } from "./flywalk-to-pano-animation";

/**
 * Computes a suitable fly walk animation to transition the camera to a Photo 2D, and sets it via the given function
 *
 * @param main The 2D photo that is target for the animation
 * @param previousMain The iElements that is rendered at the start of the animation
 * @param renderingAnimation The rendering animation for flywalk mode. Rendering
 * animation parameters are also edited by this function.
 * @param camera The camera used to render the scene
 * @param getState Function to get the current state of the app store
 * @param setFlyWalkAnimation Function to set the current flywalk animation
 * @param endAnimation Function to call when the animation is finished
 */
export function runToPhotoAnimation(
  main: IElementImg2d,
  previousMain: FlyWalkSceneActiveElement | null,
  renderingAnimation: FlyWalkRenderingAnimation,
  camera: Camera,
  getState: () => RootState,
  setFlyWalkAnimation: (animation: FlyWalkAnimation | null) => void,
): void {
  const cameraAnimation = selectAnimationToPhoto(
    main,
    isPerspectiveCamera(camera),
    computeAspectRatio(camera),
  )(getState());

  runToImgAnimation(
    cameraAnimation,
    camera,
    renderingAnimation,
    previousMain,
    setFlyWalkAnimation,
    true,
  );
}

const selectAnimationToPhoto = curryAppSelector(
  createSelector(
    [
      selectIElementsSlice,
      (state: OnlyIElementsStore, photo: IElementImg2d) => photo,
      (
        state: OnlyIElementsStore,
        photo: IElementImg2d,
        isPerspective: boolean,
      ) => isPerspective,
      (
        state: OnlyIElementsStore,
        photo: IElementImg2d,
        isPerspective: boolean,
        aspectRatio: number,
      ) => aspectRatio,
    ],
    computeAnimationToPhoto,
  ),
);

function computeAnimationToPhoto(
  iElements: IElementsState,
  photo: IElementImg2d,
  isPerspective: boolean,
  aspectRatio: number,
): CameraAnimationProps {
  const pose = selectIElementWorldTransform(photo.id)({ iElements });

  const quaternion = new Quaternion().fromArray(pose.quaternion);
  const position = parseVector3(pose.position);

  const maxPixelSize = Math.max(photo.pixelWidth, photo.pixelHeight);
  const photoHeight =
    (PHOTO_SIZE * pose.scale[0] * photo.pixelHeight) / maxPixelSize;
  position.add(
    new Vector3(0, 0, photoHeight * 0.5 * INV_TAN).applyQuaternion(quaternion),
  );

  let frustum: OrthoFrustum | undefined;
  if (!isPerspective) {
    frustum = {
      top: photoHeight * 0.5,
      bottom: -photoHeight * 0.5,
      left: -photoHeight * 0.5 * aspectRatio,
      right: photoHeight * 0.5 * aspectRatio,
      bboxCenter: parseVector3(pose.position),
    };
  }

  return {
    position,
    quaternion,
    duration: ANIMATION_DURATION,
    fov: isPerspective ? DEFAULT_PERSP_FOV : undefined,
    frustum,
  };
}

const INV_TAN = 1.0 / Math.tan(DEFAULT_PERSP_FOV * 0.5 * DEG_TO_RAD);

function computeAspectRatio(camera: Camera): number {
  if (isPerspectiveCamera(camera)) {
    return camera.aspect;
  } else if (isOrthographicCamera(camera) && camera.top !== 0) {
    return camera.right / camera.top;
  }
  return 1.0;
}
